import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { useCallback } from 'react';
import { apiScopes } from './msalConfig';

/**
 * Hook that provides a function to acquire an access token for the StaffLens API.
 *
 * Tries silent acquisition first (using cached/refresh token). If that fails
 * because user interaction is required (e.g. consent, MFA), falls back to a
 * redirect-based flow.
 *
 * Usage:
 *   const { getToken } = useAuthToken();
 *   const token = await getToken();
 *   // token is the Bearer access token string, or null if unauthenticated
 */
export function useAuthToken() {
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiScopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Silent acquisition failed — redirect to Entra ID for interactive login
        await instance.acquireTokenRedirect({
          ...apiScopes,
          account,
        });
        // After redirect, the page will reload and handleRedirectPromise
        // in AuthProvider will process the result.
        return null;
      }
      throw error;
    }
  }, [instance, accounts]);

  return { getToken };
}

/**
 * Standalone (non-hook) function to acquire a token using the singleton MSAL instance.
 * Useful for Axios interceptors or other non-React contexts.
 *
 * Import the msalInstance from AuthProvider:
 *   import { msalInstance } from './AuthProvider';
 *   import { acquireTokenForApi } from './useAuthToken';
 *   const token = await acquireTokenForApi(msalInstance);
 */
export async function acquireTokenForApi(
  msalInstance: IPublicClientApplication
): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({
        ...apiScopes,
        account,
      });
      return null;
    }
    throw error;
  }
}
